import Decimal from 'decimal.js';
import BigNumber from 'bignumber.js';
import numeral from 'numeral';

export const numberWithCommas = (
  x?: number | Decimal | null | BigNumber | string,
  precision?: number,
): string => {
  if (x === undefined || x === null || isNaN(+x)) {
    return '-';
  }
  if (typeof x === 'string') x = +x;
  let parts: string[];
  if (precision !== undefined) {
    parts = x.toFixed(precision).split('.');
  } else {
    parts = x.toString().split('.');
  }

  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};

// n 数字
// d 小数位数
export const numberWithUnit = (n?: number | null, d = 2) => {
  if (n === undefined || n === null) return '-';
  return numeral(n)
    .format(`($0.${new Array(d).fill('0').join('')}a)`)
    .toLocaleUpperCase();
};

export const numSeparate = (
  x?: number,
  precision?: number,
  removeSubfixZero = false,
  keepPositive = false,
  thousandsSeparator = true,
) => {
  if (x === undefined || x === null) {
    return '-';
  }
  if (x === 0) return '0';

  if (Math.abs(x) > 0 && Math.abs(x) < 0.00005) {
    return `< ${x > 0 ? '' : '-'}0.0001`;
  }
  const _precision = precision ?? 2;
  const num = removeSubfixZero ? Number(x.toFixed(_precision)).toString() : x.toFixed(_precision);

  const parts: string[] = num.split('.');
  if (thousandsSeparator) {
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  return `${keepPositive && x > 0 ? '+' : ''}${parts.join('.')}`;
};

export function ProfileUSDValue(
  value: number | string | Decimal | null | undefined,
  precision = 0,
  billion = false,
) {
  let res = numberWithCommas(value, precision);
  if (value === 0) return `$0`;
  if (res === '-' || value === undefined || value === null) return res;

  if (billion && Number(value) > 1e9) {
    res = formatNum(Number(value));
  }
  return `$${res}`;
}

export function BigNumberFixed(v: BigNumber | number, precision = 20) {
  if (typeof v === 'number') {
    v = new BigNumber(v);
  }
  return v.toFixed(precision).replace(/0*$/, '').replace(/\.$/, '');
}

// console.log(validNumberFive(new BigNumber(`973496329194833507266174`).dividedBy(10e18)))
export function validNumberFive(v: BigNumber | number) {
  if (!(v instanceof BigNumber)) {
    v = new BigNumber(v);
  }
  if (!v || v.isNaN()) return '';
  const strv = v.toFixed();
  return getNotZeroNumber(strv);
}

// console.log(getNotZeroNumber('229367580036N62503'))

function getNotZeroNumber(str: string, precision = 5) {
  if (!str || isNaN(Number(str))) return '';
  let startIdx = -1;
  let i = 0;
  // 首先找出从第一个非零数字开始的 n 个连续的数字，认为只有这些数字才是有效数字
  for (let charAt of str.split('')) {
    if (charAt !== '0' && charAt !== '.') {
      startIdx = i;
      break;
    }
    i++;
  }
  let vaildStrs = str.split('');
  let lastIdx = startIdx + precision - 1;
  if (str.substring(startIdx, lastIdx + 1).indexOf('.') !== -1) {
    lastIdx++;
  }
  const vaildNext = vaildStrs[lastIdx + 1];
  const vaildNext2 = vaildStrs[lastIdx + 2];
  const addLast = () => {
    vaildStrs[lastIdx] = Number(vaildStrs[lastIdx]) + 1 + '';
  };
  // 如果最后一个有效数字后面紧跟的数字大于 5，或者紧跟着 5 但后面还有其它非零数字，那么将最后一个有效数字加 1
  if (
    (vaildNext && Number(vaildNext) > 5) ||
    (vaildNext2 && vaildNext2 !== '0' && vaildNext === '5')
  ) {
    addLast();
    // 如果最后一个有效数字后面紧跟着数字 5，而且后面没有其它数字或者都是 0 四色五入
  } else if (vaildNext === '5' && (!vaildNext2 || vaildNext2 === '0')) {
    addLast();
  }

  const vaildStr = transZero(vaildStrs);
  const dotBefore = vaildStr.split('.')[0];
  let dotAfter = vaildStr.split('.')[1] || '';

  // 将小数点前的所有非有效数字替换为 0
  i = 0;
  const dotBeforeArr = [];
  for (let charAt of dotBefore.split('')) {
    if (i > lastIdx) {
      charAt = '0';
    }
    dotBeforeArr.push(charAt);
    i++;
  }

  // 将小数点后的所有非有效数字删除（不能替换为 0）
  i = dotBefore.length + 1;
  const dotAfterArr = [];
  if (dotAfter) {
    for (let charAt of dotAfter.split('')) {
      if (i > lastIdx) {
        charAt = '';
      }
      charAt && dotAfterArr.push(charAt);
      i++;
    }
  }

  // 判断是否有进位
  return dotBeforeArr.join('') + (dotAfterArr.length ? '.' + dotAfterArr.join('') : '');
}

function transZero(strs: string[]) {
  let ii = 0;
  while (strs.find(v => v.length > 1)) {
    ii++;
    if (ii > 100) break;
    const tparr = [...strs];
    for (let i = tparr.length - 1; i >= 0; i--) {
      if (tparr[i].length > 1) {
        if (!tparr[i - 1]) {
          tparr.unshift('1');
          i++;
        } else {
          let _idx = i - 1;
          if (tparr[_idx] === '.') {
            _idx--;
          }
          tparr[_idx] = +tparr[_idx] + 1 + '';
        }
        tparr[i] = '0';
      }
    }
    strs = tparr;
  }
  return strs.join('');
}

export const abbreviateNumber = (num: any, fixed = 1) => {
  if (num === null) {
    return null;
  } // terminate early
  if (num === 0) {
    return '0';
  } // terminate early
  fixed = !fixed || fixed < 0 ? 0 : fixed; // number of decimal places to show
  var b = num.toPrecision(2).split('e'), // get power
    k = b.length === 1 ? 0 : Math.floor(Math.min(b[1].slice(1), 14) / 3), // floor at decimals, ceiling at trillions
    c = k < 1 ? num.toFixed(0 + fixed) : (num / Math.pow(10, k * 3)).toFixed(0 + fixed), // divide by power
    d = c < 0 ? c : Math.abs(c), // enforce -0 is 0
    e = d + ['', 'K', 'M', 'B', 'T'][k]; // append power
  return e;
};

type FixDictNumber = {
  (arg: number): number;
  <T>(arg: T): T;
};

// 不进行四舍五入，只有下面的 fixDictNumber 用到
const floorFixNumber = (num: number) => {
  // 先 toFixed(4) 避免被科学计数法表示了，如 '1.2399e-14'
  return num.toFixed(4).match(/^-?\d+(?:\.\d{0,2})?/)?.[0];
};

// FIXME：只有 ranking 用到，将这个方法从通用中抽离，且只对具体 key 做 fix
export const fixDictNumber: FixDictNumber = <T>(obj?: T, rounded = false, fixedLength = 2) => {
  if (!obj) return obj;

  if (typeof obj === 'number')
    return Number(rounded ? obj.toFixed(fixedLength) : floorFixNumber(obj));

  if (Array.isArray(obj)) return obj.map(x => fixDictNumber(x));

  if (typeof obj === 'object' && !obj !== null) {
    return Object.entries(obj).reduce(
      (m, [key, value]) => ({
        ...m,
        [key]: fixDictNumber(value),
      }),
      {} as T,
    );
  }

  return obj;
};

export const splitNumberByStep = (
  num: number | string,
  step = 3,
  symbol = ',',
  forceInt = false,
) => {
  // eslint-disable-next-line prefer-const
  let [int, float] = (num + '').split('.');
  const reg = new RegExp(`(\\d)(?=(\\d{${step}})+(?!\\d))`, 'g');

  int = int.replace(reg, `$1${symbol}`);
  if (Number(num) > 1000000 || forceInt) {
    // hide the after-point part if number is more than 1000000
    float = '';
  }
  if (float) {
    return `${int}.${float}`;
  }
  return int;
};

export const numFormat = (
  num?: string | number,
  precision?: number,
  prefix = '',
  postiveSymbol = false,
  abbreviate = true,
) => {
  if (!num) {
    return num === 0 ? `${postiveSymbol ? '+' : ''}${prefix}0` : '-';
  }

  const _num = Number(num);

  if (abbreviate) {
    if (Math.abs(_num) >= 1e9) {
      return `${prefix}${Number((_num / 1e9).toFixed(1))}B`;
    }
  }

  const symbol = _num < 0 ? '-' : postiveSymbol ? '+' : '';
  const _precision = precision ?? (_num < 1 ? 4 : 2);
  const parts = Math.abs(_num).toFixed(_precision).split('.');

  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const formatted = parts.join('.');

  return `${symbol}${prefix}${formatted}`;
};

export function unreadCountFormat(num: string | number, limit = 99) {
  if (!num) {
    return '0';
  }

  const _num = Number(num);

  if (_num <= 0) {
    return '';
  }

  if (_num <= limit) {
    return _num + '';
  }

  return `${limit}+`;
}

export const calcPercent = (pre?: number, next?: number, precision = 2, needSign = true) => {
  const delta = (next || 0) - (pre || 0);
  const percent = pre ? ((delta / pre) * 100).toFixed(precision) : next ? '100.00' : '0.00';

  return `${needSign && delta >= 0 ? '+' : ''}${percent}%`;
};

/**
 * 格式化数字
 *
 * 不能保证超出 js 运算范围的数可以正常格式化
 * @param num 数字，可以为字符串，如 '1232.123'
 * @param digit 保留小数，可以定义是否要 trim 小数中的 0
 * @param param2
 * @returns string
 */
export const formatNum = (
  num?: string | number,
  digit = 2,
  {
    floor = 0.0001,
    placeholder = '-',
    prefix = '',
    keepPostiveSign = false,
    // 从哪位符号开始缩写，对应 abbrs，0-'', 1-'k', 2-'m', 3-'b'...
    abbrStart = 3,
    trimFractionZero = true,
    abbrs = ['', 'K', 'M', 'B', 'T'],
    truncate = false,
  }: {
    floor?: number | false;
    // 只有值是 undefined / NaN / '' 才会使用 placeholder
    placeholder?: string;
    prefix?: string;
    keepPostiveSign?: boolean;
    // 从哪开始缩写
    abbrStart?: number;
    trimFractionZero?: boolean;
    abbrs?: string[];
    truncate?: boolean;
  } = {},
) => {
  if (!num) {
    return num === 0
      ? `${keepPostiveSign ? '+' : ''}${prefix}${trimFractionZero ? '0' : (0).toFixed(digit)}`
      : placeholder;
  }

  const _num = Number(num);

  if (Number.isNaN(_num)) {
    return placeholder;
  }

  const sign = _num < 0 ? '-' : keepPostiveSign ? '+' : '';
  const absNum = Math.abs(_num);

  if (typeof floor === 'number' && absNum < floor) {
    return `<${sign}${prefix}${floor}`;
  }

  const pow = (Math.log10(absNum) / 3) | 0;
  const _pow = abbrStart <= pow ? pow : 0;
  const realPow = _pow < abbrs.length ? _pow : abbrs.length - 1;
  const suffix = abbrs[realPow];

  const rn = absNum / Math.pow(10, 3 * realPow);
  let roundedNum = '';

  if (truncate) {
    const _rn = rn.toString();
    const idx = _rn.indexOf('.');
    // Math.floor(rn * 100) / 100 考虑大数边界，还是通过字符串做吧
    roundedNum = idx > -1 ? _rn.slice(0, idx + digit + 1) : _rn;
  } else {
    roundedNum = rn.toFixed(digit);
  }

  const parts = roundedNum.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  parts[1] = parts[1] && trimFractionZero ? parts[1].replace(/0+$/, '') : parts[1];
  const formatted = parts[1] ? parts.join('.') : parts[0];

  return `${sign}${prefix}${formatted}${suffix}`;
};

// -> assets
export const formatNetworth = (num?: number, trimFractionZero = false) => {
  if (!num && num !== 0) {
    return '';
  }

  // >1b || <1m
  if (num > 1000000000 || num < 1000000) {
    return formatNum(num, 2, { prefix: '$', floor: 0.01, trimFractionZero });
  }

  // 1m
  return formatNum(num, 0, { prefix: '$', trimFractionZero });
};

export const formatPrice = (num?: number) => {
  if (!num && num !== 0) {
    return '';
  }

  // >1
  if (num >= 1) {
    return formatNum(num, 2, { prefix: '$', trimFractionZero: false });
  }

  const preNum = num.toPrecision(4);

  if (preNum.toString().length > 10) {
    const exNum = num.toExponential(4);

    return `$${exNum}`;
  }

  return `$${preNum}`;
};

export const formatAmount = (num?: number, keepPostiveSign?: boolean) => {
  if (!num && num !== 0) {
    return '';
  }

  const absNum = Math.abs(num);

  if (absNum < 1) {
    const sign = num >= 0 && keepPostiveSign ? '+' : '';

    if (num === 0) {
      return `${sign}0.0000`;
    }

    const preNum = num.toPrecision(4);

    if (preNum.toString().length > 10) {
      const exNum = num.toExponential(4);

      return `${sign}${exNum}`;
    }

    return `${sign}${preNum}`;
  }

  // >= 1
  // >1b || <10k
  if (absNum > 1000000000 || absNum < 10000) {
    return formatNum(num, 4, { trimFractionZero: false, keepPostiveSign });
  }

  // <1m
  if (absNum < 1000000) {
    return formatNum(num, 2, { trimFractionZero: false, keepPostiveSign });
  }

  return formatNum(num, 0, { trimFractionZero: false, keepPostiveSign });
};

// <- assets

const Sub_Numbers = '₀₁₂₃₄₅₆₇₈₉';

/**
 * 格式化数字到 str，不保留 e（科学计数） 的版本，小数形式为下标
 *
 * https://debankglobal.larksuite.com/wiki/WO2PwFCuKi7DhLkw9BauTeqYsjf
 */
export const formatNumStr = (
  num?: string | number,
  digit = 2,
  {
    floor = 0.01,
    floorHolder = '0.01',
    floorSign = '<',
    placeholder = '-',
    prefix = '',
    keepPostiveSign = false,
    zeroSubStart = 5,
  }: {
    floor?: number | false;
    floorSign?: string;
    floorHolder?: string;
    // 只有值是 undefined / NaN / '' 才会使用 placeholder
    placeholder?: string;
    prefix?: string;
    keepPostiveSign?: boolean;
    zeroSubStart?: number;
  } = {},
) => {
  if (!num || num === '0') {
    return num === 0 || num === '0' ? `${keepPostiveSign ? '+' : ''}${prefix}0` : placeholder;
  }

  let n = num.toString();
  const sign = n.charAt(0) === '-' ? ((n = n.slice(1)), '-') : keepPostiveSign ? '+' : '';
  const b = new BigNumber(n);

  if (typeof floor === 'number' && b.lte(floor)) {
    if (!zeroSubStart) {
      return `${floorSign}${sign}${prefix}${floorHolder}`;
    }

    // 4 位有效数字
    const s = b.precision(4).toFormat();
    const ss = s.replace(/^0.(0*)?(?:.*)/, (l, z) => {
      const zeroLength = z.length;

      // 不满足 5 位长度 0 的，返回 4 位有效数字精度
      if (zeroLength < zeroSubStart) {
        return l;
      }

      const sub = `${zeroLength}`
        .split('')
        .map(x => Sub_Numbers[x as any])
        .join('');

      const end = s.slice(zeroLength + 2);
      return `0.0${sub}${end}`;
    });

    return `${sign}${prefix}${ss}`;
  }

  return `${sign}${prefix}${b.decimalPlaces(digit).toFormat()}`;
};

export const formatTokenNum = (
  num?: number | string,
  prefix?: string,
  { keepPostiveSign }: { keepPostiveSign?: boolean } = {},
) => {
  return formatNumStr(num, 4, { keepPostiveSign, prefix });
};

// console.log('---formatTokenNum---', formatTokenNum(0.00017082967741977783));
// console.log('---formatTokenNum---', formatTokenNum(0.00004902123));
// console.log('---formatTokenNum---', formatTokenNum(0.0000012345));
// console.log('---formatTokenNum---', formatTokenNum(0.00000012345));
// console.log('---formatTokenNum---', formatTokenNum(11.12345e3));
// console.log('---formatTokenNum---', formatTokenNum(-11.12345e3));
// console.log('---formatTokenNum---', formatTokenNum(1111.12345));

export const formatProtocolUsd = (num?: number | string) => {
  if (Math.abs(Number(num)) >= 10e3) {
    return formatNum(num, 1, { abbrStart: 1, prefix: '$' });
  }

  return formatNumStr(num, 0, {
    zeroSubStart: 0,
    prefix: '$',
  });
};

// console.log('---formatProtocolUsd---', formatProtocolUsd(0.00000012345));
// console.log('---formatProtocolUsd---', formatProtocolUsd(11.12345e3));
// console.log('---formatProtocolUsd---', formatProtocolUsd(-11.12345e3));
// console.log('---formatProtocolUsd---', formatProtocolUsd(1111.12345));

export const formatStreamUsd = (num?: number | string) => {
  if (Math.abs(Number(num)) >= 10e3) {
    return formatNum(num, 1, { abbrStart: 1, prefix: '$' });
  }

  return formatNumStr(num, 2, {
    zeroSubStart: 0,
    prefix: '$',
  });
};

// console.log('---formatStreamUsd---', formatStreamUsd(0.00000012345));
// console.log('---formatStreamUsd---', formatStreamUsd(11.12345e3));
// console.log('---formatStreamUsd---', formatStreamUsd(-11.12345e3));
// console.log('---formatStreamUsd---', formatStreamUsd(1111.12345));

export const formatRanking = (num?: number | string) => {
  if (Math.abs(Number(num)) >= 10e3) {
    return formatNum(num, 1, { abbrStart: 1, prefix: '$' });
  }

  return formatNumStr(num, 0, {
    zeroSubStart: 0,
    floorHolder: '0',
    floorSign: '',
    prefix: '$',
  });
};

// console.log('---formatRanking---', formatRanking(0.00000012345));
// console.log('---formatRanking---', formatRanking(11.12345e3));
// console.log('---formatRanking---', formatRanking(-11.12345e3));
// console.log('---formatRanking---', formatRanking(1111.12345));
